import { isEmpty, isNull } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
  ENTRA,
  GOOGLEWORKSPACE,
  MICROSOFTAD,
} from "../../redux/constants/constants";
import {
  SET_SYS_FROM_TIME_STAMP,
  SET_SYS_TO_TIME_STAMP,
} from "./sysGuardConstants";
import { DateRange, DefinedRange } from "react-date-range";
import { Popover, Input, InputBase, Grid, makeStyles } from "@material-ui/core";
import {
  createStaticRange,
  dateRangePickerStaticList,
} from "../../components/common/utilities";
import { useEffect, useMemo, useState } from "react";
import {
  set,
  format,
  differenceInCalendarDays,
  differenceInMonths,
  differenceInYears,
} from "date-fns";
import {
  defineds,
  selectionRange,
} from "../../components/common/Navbar/AppDateTimeRange";
import { Button } from "material-ui-core";

export const maskUsername = (input) => {
  if (!input || typeof input !== "string") return "*"; // Return empty string if input is missing or not a string

  // Detect if input is an email address
  if (input.includes("@") && input.includes(".")) {
    const [localPart, domain] = input.split("@");
    if (!localPart || !domain) return input; // Ensure valid email structure

    const [domainName, topLevelDomain] = domain.split(".");
    if (!domainName || !topLevelDomain) return input; // Handle invalid domain

    const maskedLocal = localPart[0] + "*".repeat(localPart.length - 1);
    const maskedDomain = domainName[0] + "*".repeat(domainName.length - 1);
    return `${maskedLocal}@${maskedDomain}.${topLevelDomain}`;
  }

  // Detect if input contains a dot but is not an email (e.g., "John.Doe")
  if (input.includes(".") && !input.includes("@")) {
    return input
      .split(".")
      .map((part) =>
        part.length > 0 ? part[0] + "*".repeat(part.length - 1) : ""
      )
      .join(".");
  }

  // Handle names with spaces (assumed first and last name)
  const nameParts = input.split(" ");
  if (nameParts.length > 1) {
    const mask = (name) =>
      name.length > 3
        ? name.slice(0, 3) + "*".repeat(name.length - 3)
        : name[0] + "*".repeat(name.length - 1);
    return `${mask(nameParts[0])} ${mask(nameParts[nameParts.length - 1])}`;
  }

  // Single-word names
  return input.length > 3
    ? input.slice(0, 3) + "*".repeat(input.length - 3)
    : input[0] + "*".repeat(Math.max(input.length - 1, 0)); // Ensure safe masking
};

export const handleJiraDateRangeDiff = (
  endDate,
  startDate,
  setDateRangeDiff
) => {
  if (!startDate || !endDate) {
    setDateRangeDiff("0D");
    return;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Normalize dates to ignore time
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const days = differenceInCalendarDays(end, start) + 1; // include start day

  let result = "";

  if (days < 30) {
    result = `${days}D`;
  } else {
    let months = differenceInMonths(end, start);
    if (months === 0) months = 1; // treat partial month as 1M

    if (months < 12) {
      result = `${months}M`;
    } else {
      let years = differenceInYears(end, start);
      if (years === 0) years = 1; // treat partial year as 1Y
      result = `${years}Y`;
    }
  }

  setDateRangeDiff(result);
};

export const useSysGuardDataSource = (datasourceId = null) => {
  const dataSourcesById = useSelector(
    (state) => state.SysGuardReducer.sysGuardDataSource
  );
  const editingDatasourceID = useSelector(
    (state) => state.SysGuardReducer.editingDatasourceID
  );

  // If a specific datasource ID is provided (either as param or from Redux), return that
  const targetId = datasourceId || editingDatasourceID;
  if (targetId && dataSourcesById[targetId]) {
    return dataSourcesById[targetId];
  }

  // Convert the object to array and find the first connected or available data source
  const dataSources = Object.values(dataSourcesById);
  
  // Prioritize ENTRA, then GOOGLEWORKSPACE, then MICROSOFTAD
  const entraSource = dataSources.find(ds => ds?.typeCode === ENTRA);
  const googleSource = dataSources.find(ds => ds?.typeCode === GOOGLEWORKSPACE);
  const msadSource = dataSources.find(ds => ds?.typeCode === MICROSOFTAD);

  return !isNull(entraSource) && !isEmpty(entraSource)
    ? entraSource
    : googleSource || msadSource;
};

export const getClassNamesByType = (type) => {
  const isEntra = type === ENTRA;
  const isMSAD = type === MICROSOFTAD;

  return {
    iconClassName: isEntra ? "entra-icon-img" : "sys-guard-dataSource-icon-img",
    containerClassName: isEntra
      ? "entra-img-container"
      : isMSAD
      ? "msad-img-container"
      : "dataSource-icon",
    title: isEntra ? "Entra" : "Google Workspace",
  };
};

export const renderRiskFilterClassName = (value) => {
  const riskClasses = {
    high: "sysGuardFilterLocation highRisk",
    medium: "sysGuardFilterLocation mediumRisk",
    "no risk": "sysGuardFilterLocation none",
  };

  return riskClasses[value?.toLowerCase()] || "sysGuardFilterLocation lowRisk";
};

const useStyles = makeStyles((theme) => ({
  title: {
    display: "none",
    [theme.breakpoints.up("sm")]: {
      display: "block",
    },
  },
  search: {
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "white",
    color: "#081981",
    "&:hover": {
      backgroundColor: "white",
    },
    marginLeft: 0,
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "auto",
    },
  },
}));

export const DateRangeSelector = ({
  fromTimeStamp,
  toTimeStamp,
  fromAction,
  toAction,
  defaultRange = {
    start: defineds.startOfLastSevenDay.getTime(),
    end: defineds.endOfToday.getTime(),
  },
  label = "Time Period",
  loc,
}) => {
  const dispatch = useDispatch();
  const customStyles = useStyles();
  const [dateRangeDiff, setDateRangeDiff] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [fromDateValue, setFromDateValue] = useState(new Date());
  const [toDateValue, setToDateValue] = useState(new Date());
  const [selectionRangeDates, setSelectionRangeDates] =
    useState(selectionRange);
  const isMDRDashboard = loc === "MDRDashboard";
  const today = new Date();
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(today.getMonth() - 3);

  const open = Boolean(anchorEl);
  const id = open ? "date-range-popover" : undefined;

  const staticRanges = useMemo(
    () => [
      ...createStaticRange(
        dateRangePickerStaticList(isMDRDashboard ? "mdr" : label)
      ),
    ],
    [label]
  );

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopup = () => {
    setAnchorEl(null);
  };

  const handleClose = () => {
    const start = fromDateValue.getTime();
    const end = toDateValue.getTime();
    setAnchorEl(null);
    dispatch(fromAction(start));
    dispatch(toAction(end));
  };

  const updateDateRange = (startDate, endDate) => {
    setFromDateValue(startDate);
    setToDateValue(endDate);
    handleJiraDateRangeDiff(endDate, startDate, setDateRangeDiff);
    setSelectionRangeDates({ startDate, endDate });
  };

  useEffect(() => {
    const startDate = isNull(fromTimeStamp)
      ? defaultRange.start
      : fromTimeStamp;
    const endDate = isNull(toTimeStamp) ? defaultRange.end : toTimeStamp;

    updateDateRange(new Date(startDate), new Date(endDate));

    dispatch(fromAction(startDate));
    dispatch(toAction(endDate));
  }, []);

  const handleSelect = (ranges, staticSelection) => {
    const [selectionKey] = Object.keys(ranges);
    if (!selectionKey) return;

    let { startDate, endDate } = ranges[selectionKey];

    const isEndDateMidnight =
      endDate.getHours() === 0 &&
      endDate.getMinutes() === 0 &&
      endDate.getSeconds() === 0;

    if (isEndDateMidnight) {
      startDate = set(new Date(startDate), {
        hours: 0,
        minutes: 0,
        seconds: 0,
      });
      endDate = set(new Date(endDate), { hours: 23, minutes: 59, seconds: 59 });
    }

    updateDateRange(startDate, endDate);

    if (staticSelection) {
      setAnchorEl(null);
      dispatch(fromAction(startDate.getTime()));
      dispatch(toAction(endDate.getTime()));
    }
  };

  return (
    <div className={customStyles.search}>
      <div
        className={
          isMDRDashboard
            ? "dateRange-search-box-sysGuard MDRDashboard"
            : "dateRange-search-box-sysGuard "
        }
        onClick={handleClick}
      >
        <span
          className={
            isMDRDashboard ? "selected-date-value MDR" : "selected-date-value"
          }
          title={`${label}: ${dateRangeDiff}`}
        >
          {dateRangeDiff}
        </span>
        <div className="dateRange-pill-container" onClick={handleClick}>
          <span className={isMDRDashboard ? "pill-date mdr" : "pill-date"}>
            {format(fromDateValue, "MMM dd, yyyy")}
          </span>
          <span
            className={isMDRDashboard ? "pill-separator mdr" : "pill-separator"}
          />
          <span className={isMDRDashboard ? "pill-date mdr" : "pill-date"}>
            {format(toDateValue, "MMM dd, yyyy")}
          </span>
        </div>
      </div>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={closePopup}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className="dateRange-time-popover"
      >
        <div className="dateRange-time-picker">
          <div className="dateRanger-header">
            <span className="quick-range">Relative Range</span>
            <span className="select-range">Select a Range</span>
          </div>
          <div style={{ display: "flex" }}>
            <DefinedRange
              onChange={(ranges) => handleSelect(ranges, true)}
              ranges={[selectionRangeDates]}
              staticRanges={staticRanges}
              inputRanges={[]}
              maxDate={today}
              minDate={isMDRDashboard ? threeMonthsAgo : undefined}
            />
            <DateRange
              onChange={(ranges) => handleSelect(ranges)}
              ranges={[selectionRangeDates]}
              staticRanges={staticRanges}
              inputRanges={[]}
              retainEndDateOnFirstSelection
              maxDate={today}
              minDate={isMDRDashboard ? threeMonthsAgo : undefined}
            />
          </div>
        </div>
        <div className="dateRange-apply-btn">
          <button className="custom-apply-button" onClick={handleClose}>
            Apply
          </button>
        </div>
      </Popover>
    </div>
  );
};
